<template>
  <v-select
    v-model="selectedFilters"
    label="Search Filters"
    :items="availableFilters"
    item-text="name"
    item-value="key"
    background-color="input"
    :menu-props="{ offsetY: true, maxHeight: '600' }"
    multiple
    outlined
    hide-details
    small-chips
    dense>
    <template v-slot:selection="{ attrs, item }">
      <v-chip
        v-bind="attrs"
        :color="item.color || null"
        :dark="Boolean(item.color)"
        small>
        {{ getFilterText(item) }}
        <v-icon
          small
          right
          @click="removeFilter(item.key, true)">
          $delete
        </v-icon>
      </v-chip>
    </template>
    <template v-slot:item="{ item }">
      <v-list-item>
        <v-list-item-content>
          <v-text-field
            :label="item.name"
            @click:clear="removeFilter(item.key)"
            @keyup.stop="handleInputValue(item)"
            background-color="input"
            class="pl-3"
            clearable
            dense
            hide-details
            outlined
            v-if="item.type === 'text'"
            v-model="params[`${item.key}`]">
          </v-text-field>
          <v-autocomplete
            @click="activeInput = item.key"
            :items="getItemsForKey(item.key)"
            :label="item.name"
            :menu-props="{ maxHeight: 225 }"
            @change="handleInputValue(item)"
            @click:clear="removeFilter(item.key)"
            auto-select-first
            background-color="input"
            class="pl-3"
            clearable
            dense
            hide-details
            :item-text="getItemTextForKey(item.key)"
            :item-value="getItemValueForKey(item.key)"
            outlined
            v-if="item.type === 'select'"
            v-model="params[`${item.key}`]">
          </v-autocomplete>
          <v-checkbox
            @change="handleInputValue(item)"
            class="pl-3 mt-0"
            hide-details
            :label="item.name"
            v-if="item.type === 'boolean'"
            v-model="params[`${item.key}`]">
          </v-checkbox>
        </v-list-item-content>
      </v-list-item>
    </template>
  </v-select>
</template>

<script>
import { debounce } from 'lodash'

export default {
  name: 'SearchFilters',
  props: {
    departments: {
      type: Array
    },
    pages: {
      type: Array
    },
    feature_type: {
      type: Array
    },
    feature_text: {
      type: Array
    },
    order_page: {
      type: Boolean
    }
  },
  data () {
    return {
      activeInput: '',
      selectedFilters: [],
      filters: [
        { name: 'Page', key: 'page_name', type: 'select' },
        { name: 'Feature Type', key: 'position_name', type: 'select', tenants: ['alliance-retail-group'] },
        { name: 'Feature Text', key: 'feature_text', type: 'select', tenants: ['alliance-retail-group'] },
        { name: 'Department', key: 'department', type: 'select' },
        { name: 'Item ID', key: 'item_id', type: 'text' },
        { name: 'Shippers/Pallets', key: 'is_shipper', type: 'boolean' },
        { name: 'Items With Orders', key: 'has_orders', type: 'boolean', tenants: ['alliance-retail-group'], order_grid_only: true },
        { name: 'Features With Notes', key: 'has_notes', type: 'boolean', tenants: ['alliance-retail-group'], order_grid_only: true }
      ],
      params: {
        page_name: '',
        item_id: '',
        department: '',
        is_shipper: null
      }
    }
  },
  computed: {
    activeFilters () {
      return Object.keys(this.params).filter(this.isActive)
    },
    availableFilters () {
      const tenant_filters = this.filters.filter(f => (!f.tenants || f.tenants.includes(this.$auth.tenant)))
      const page_filters = tenant_filters.filter(f => (!f.order_grid_only || f.order_grid_only === this.order_page))
      return page_filters
    }
  },
  watch: {
    activeFilters: {
      handler: debounce(function (newValue) {
        if (newValue) {
          this.updateSearchFilters()
        }
      }, 400),
      deep: true
    }
  },
  created() {
    if (sessionStorage.search_filters) {
      const stored = JSON.parse(sessionStorage.search_filters)
      this.params = { ...this.params, ...stored }
      const activeKeys = Object.keys(stored).filter(key => Boolean(stored[key]))
      if (activeKeys.length > 0) this.selectedFilters.push(...activeKeys)
    }
  },
  methods: {
    getItemsForKey (key) {
      switch (key) {
        case 'page_name': return this.pages
        case 'department': return this.departments
        case 'position_name': return this.feature_type
        case 'feature_text': return this.feature_text
        default: return []
      }
    },
    getItemTextForKey (key) {
      switch (key) {
        case 'page_name': return 'page_name'
        case 'department': return 'department'
        case 'position_name': return 'position_name'
        case 'feature_text': return 'feature_text'
        default: return ''
      }
    },
    getItemValueForKey (key) {
      return this.getItemTextForKey(key)
    },
    isActive (key) {
      if (this.isSelected(key) && Boolean(this.params[key])) {
        const value = this.params[key]
        return Array.isArray(value) ? (value.length > 0) : true
      }
      return false
    },
    getFilterText (item) {
      if (item.key === 'is_shipper') {
        return item.name
      } else {
        return `${item.name}: "${this.params[item.key]}"`
      }
    },
    removeFilter (key, clearValue = false) {
      const index = this.selectedFilters.indexOf(key)
      if (index > -1) {
        this.selectedFilters.splice(index, 1)
        if (clearValue) this.params[key] = null
      }
    },
    handleInputValue: debounce(function (item) {
      if (item.type === 'boolean' && !this.params[item.key]) {
        this.removeFilter(item.key, true)
      }
      if (Boolean(this.params[item.key]) && !this.isSelected(item.key)) {
        this.selectedFilters.push(item.key)
      }
    }, 200),
    isSelected (key) {
      return this.selectedFilters.includes(key)
    },
    updateSearchFilters () {
      const filtersObj = this.activeFilters.reduce((obj, key) => {
        obj[key] = this.params[key]
        return obj
      }, {})
      this.$emit('input', filtersObj)
      if (this.activeFilters.length > 0) {
        sessionStorage.setItem('search_filters', JSON.stringify(filtersObj))
      } else {
        sessionStorage.removeItem('search_filters')
      }
    }
  }
}
</script>
